import { useEffect, useState } from "react";
import { T } from "@/components/T";
import { useParams, Link } from "react-router-dom";
import { fetchService } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { SERVICE_BY_KIND } from "@/lib/categories";
import { resolveServiceImage } from "@/lib/images";
import BookingForm from "@/components/BookingForm";
import { ChevronRight, Check, Clock } from "lucide-react";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { pick, isDeva } = useLang();
  const [svc, setSvc] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setSvc(null);
    fetchService(slug).then(setSvc).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="mx-auto max-w-3xl px-5 py-32 text-center text-ink-3"><T>Service not found.</T></div>;
  if (!svc) return <div className="mx-auto max-w-3xl px-5 py-32 text-center text-ink-3">…</div>;

  const meta = SERVICE_BY_KIND[svc.kind] || { deva: svc.kind, en: svc.kind, form: "venue" };
  const listPath = `/services/${svc.kind}`;
  const listLabel = pick(meta.deva, meta.en);

  return (
    <article className="pb-24">
      <header className="border-b border-line bg-[var(--surface)]">
        <div className="mx-auto max-w-6xl px-5 py-12 md:px-8 md:py-14">
          <nav className="mb-6 flex items-center gap-1.5 text-xs text-ink-3">
            <Link to="/" className="hover:text-saffron"><T>Dharmartha</T></Link>
            <ChevronRight className="h-3 w-3" />
            <Link to={listPath} className="hover:text-saffron">{listLabel}</Link>
          </nav>
          <p className="overline-label text-gold">{listLabel}{svc.deity ? ` · ${svc.deity}` : ""}</p>
          <h1 className={`mt-3 ${isDeva ? "font-deva" : "font-serif-latin"} text-4xl text-ink deva-lh md:text-5xl`}>{pick(svc.devanagari_name, svc.name)}</h1>
        </div>
      </header>

      <div className="mx-auto grid max-w-6xl gap-12 px-5 py-12 md:grid-cols-12 md:px-8 md:py-16">
        <div className="md:col-span-7">
          {svc.image_url !== null && <img src={resolveServiceImage(svc)} alt={svc.name} className="mb-8 h-64 w-full object-cover" />}
          <p className="text-lg leading-relaxed text-ink-2">{svc.long_description || svc.description}</p>

          {svc.includes?.length > 0 && (
            <div className="mt-8">
              <p className="overline-label mb-4 text-ink-3"><T>What's included</T></p>
              <ul className="space-y-3">
                {svc.includes.map((it, i) => (
                  <li key={i} className="flex items-start gap-3 text-ink-2">
                    <Check className="mt-1 h-4 w-4 shrink-0 text-saffron" />{it}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-6 border-t border-line pt-6 text-sm text-ink-2">
            {svc.duration && <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-gold" /> {svc.duration}</span>}
            {svc.occasion && <span>Occasion: {svc.occasion}</span>}
          </div>
          {svc.suggested_offering && (
            <p className="mt-6 border-l-2 border-[var(--gold)] bg-[var(--surface-alt)]/40 px-5 py-4 text-sm italic text-ink-2">
              {svc.suggested_offering}
            </p>
          )}
        </div>

        <div className="md:col-span-5">
          <div className="md:sticky md:top-24">
            <BookingForm bookingType={svc.kind} formType={meta.form} service={svc} />
          </div>
        </div>
      </div>
    </article>
  );
}
